import { Chess, Move } from "chess.js"
import { useCallback, useEffect, useState } from "react"
import { useEngine } from "./useEngine"
import { ENGINE_IS_READY } from "./messages"

/** Chess engine's status. */
export enum ChessEngineStatus {
  Loading,
  Ready,
  Running,
  Waiting,
}

/**
 * Latest chess move found by the chess engine.
 * `null` means the engine has not been running.
 */
export type ChessEngineMove = { from: string; to: string; promotion?: string } | null

export type Result =
  | {
      depth: number
      advantage?: "w" | "b"
      score?: string | number
      moves?: Move[]
    }
  | undefined

type MoveAnalysis = {
  depth: number
  advantage?: "w" | "b"
  score?: string | number
  moves?: Move[]
}

export const useStockfish = (fen: string | undefined, chess960 = false) => {
  const { setHandler, command } = useEngine(!!fen)
  const [results, setResults] = useState<{ [fen: string]: Result }>({})
  const [status, setStatus] = useState(ChessEngineStatus.Loading)
  const isAnalyzing = status === ChessEngineStatus.Running

  useEffect(() => {
    if (!fen) return

    if (fen) {
      command(`setoption name UCI_Chess960 value ${chess960}`)
      command(`position fen ${fen}`)
      command("go depth 16 movetime 30000")
    }
  }, [fen, chess960, command])

  /** `onmessage` handler for the evaluator. */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!fen) return

      const line: string = typeof event === "object" ? event.data : event

      if (ENGINE_IS_READY.test(line))
        return setStatus(ChessEngineStatus.Waiting)

      setStatus(ChessEngineStatus.Running)

      // Check cached result
      // Continue setting more results for fen if full depth is not reached yet
      const handleEngineResult = (result: MoveAnalysis) => {
        const cachedResult = results[fen]

        if (!cachedResult || cachedResult.depth < result.depth) {
          setResults((prevResults) => ({
            ...prevResults,
            [fen]: result,
          }))
        }
      }

      const bestMoveMatch = line.match(
        /^bestmove ([a-h][1-8])([a-h][1-8])([qrbk])?/
      )
      const depthMatch = line.match(/^info .*\bdepth (\d+)/)

      // Upper and lowerbound are the score bounds during an incomplete search
      // We are not interested in them
      const boundMatch = line.match(
        /^info .*\b(lowerbound|upperbound) (\w+) (-?\d+)/
      )

      // We're done analyzing this move
      // This runs last
      if (bestMoveMatch) return setStatus(ChessEngineStatus.Waiting)

      // Let's see if we got some info
      if (depthMatch && !boundMatch) {
        const game = new Chess(fen, { chess960 })

        const depth = parseInt(depthMatch[1])

        const analyzedDepth = results[fen]?.depth || 0

        if (depth < analyzedDepth) return setStatus(ChessEngineStatus.Waiting)

        const analysis: MoveAnalysis = { depth }

        const scoreMatch = line.match(/^info .*\bscore (\w+) (-?\d+)/)
        const movesMatch = line.match(/^info .*\bpv ([a-h][1-8][qrbk]?.*)/)

        if (!scoreMatch) return

        if (scoreMatch) {
          let score: string | number =
            parseInt(scoreMatch[2]) * (game.turn() === "w" ? 1 : -1)

          analysis.advantage = game.isCheckmate()
            ? game.turn() === "w"
              ? "b"
              : "w"
            : score >= 0
              ? "w"
              : "b"

          if (game.isCheckmate()) {
            score = "#1"
            setStatus(ChessEngineStatus.Waiting)
          } else if (scoreMatch[1] === "cp") {
            // Is it measuring in centipawns?
            score = (score / 100.0).toFixed(2)
          }

          analysis.score = score
        }

        if (movesMatch) {
          const partialMoves: Array<{ from: string; to: string; promotion?: string }> = movesMatch[1]
            .split(" ")
            .map((move: any) => {
              const match = move.match(/([a-h][1-8])([a-h][1-8])([qrbk])?/)
              return { from: match[1], to: match[2], promotion: match[3] }
            })

          // We need the SAN in the response, so let's pass the moves through the game.
          // chess.js throws on invalid object-format moves instead of returning null.
          // Break on first failure since subsequent moves depend on prior board state.
          const moves: Move[] = []
          for (const move of partialMoves) {
            try {
              moves.push(game.move(move))
            } catch (e) {
              if (e instanceof Error && e.message.startsWith("Invalid move:")) {
                break
              }
              throw e
            }
          }

          analysis.moves = moves
        }

        handleEngineResult(analysis)
      }
    },
    [fen, results, chess960]
  )

  // Tell the engine that the GUI is done processing.
  useEffect(() => {
    command("isready")
  }, [command])

  // Add `onmessage` handler to the engine
  useEffect(() => {
    setHandler(handleMessage)
  }, [setHandler, handleMessage])

  // Reset the engine on unmount.
  useEffect(() => {
    return () => command("quit")
  }, [command])

  return { results: fen ? results[fen] : undefined, isAnalyzing }
}
